import { ApiError } from '@/shared/types/api.types';

/**
 * Uploads one file to presigned S3 part URLs.
 *
 * Bytes go straight to storage, never through Core: Core opens the upload session, signs
 * part URLs and completes the upload, and this engine moves the parts in between. It has
 * no UI and no feature knowledge, so the documents upload queue and anything later (model
 * files, attachments) drive the same code.
 */

/** A part storage already holds, as the session's `uploadedParts` reports it. */
export interface StoredPart {
  readonly partNumber: number;
  readonly etag: string;
  readonly sizeBytes: number;
}

export interface CompletedPart {
  readonly partNumber: number;
  readonly etag: string;
}

/** The slice of an upload session the engine needs. */
export interface MultipartPlan {
  readonly partSizeBytes: number;
  readonly partCount: number;
  readonly uploadedParts: readonly StoredPart[];
}

/** Signs URLs for some parts; Core signs at most `URL_BATCH_SIZE` per call. */
export type PartUrlProvider = (
  partNumbers: readonly number[],
  signal: AbortSignal
) => Promise<readonly { readonly partNumber: number; readonly url: string }[]>;

export interface PartTransport {
  /**
   * PUTs one part and resolves with the ETag storage returned. Rejects with an `ApiError`,
   * or with the signal's reason once aborted.
   */
  put(
    url: string,
    body: Blob,
    options: { readonly signal: AbortSignal; readonly onProgress: (sentBytes: number) => void }
  ): Promise<string>;
}

export interface UploadProgress {
  readonly sentBytes: number;
  readonly totalBytes: number;
}

export interface MultipartUploadOptions {
  readonly file: Blob;
  readonly plan: MultipartPlan;
  readonly partUrls: PartUrlProvider;
  readonly transport: PartTransport;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: UploadProgress) => void;
  /** Test seams: the backoff wait and its jitter source. */
  readonly wait?: (ms: number, signal: AbortSignal) => Promise<void>;
  readonly random?: () => number;
}

export const PARTS_IN_FLIGHT = 4;
export const ATTEMPTS_PER_PART = 3;
export const URL_BATCH_SIZE = 100;
const BACKOFF_BASE_MS = 500;

/** Why `signal` was aborted, as the `Error` a rejected promise should carry. */
export function abortReason(signal: AbortSignal): Error {
  const reason: unknown = signal.reason;
  // jsdom's DOMException does not extend Error, as browsers' does.
  return reason instanceof Error || reason instanceof DOMException
    ? reason
    : new DOMException('The upload was aborted.', 'AbortError');
}

/** Resolves after `ms`, or rejects with the abort reason as soon as `signal` aborts. */
export function waitFor(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortReason(signal));
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(abortReason(signal));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Network errors, timeouts and 5xx can pass on a second try; a 4xx will not. */
function isRetryable(error: unknown): boolean {
  return error instanceof ApiError && error.isRetryable;
}

/** Presigned URLs expire; storage then answers 403 and a newly signed URL fixes it. */
function isRefusedUrl(error: unknown): boolean {
  return error instanceof ApiError && error.kind === 'forbidden';
}

/**
 * Uploads the parts storage does not hold yet and returns every part's ETag, in order, for
 * the session's `complete` call.
 *
 * Four parts are in flight at once. A part gets three attempts with jittered backoff, and
 * one fresh URL if storage refuses the one it has. The first part that cannot be uploaded
 * aborts the others and fails the upload with its error; aborting `signal` does the same
 * with the signal's reason.
 */
export async function uploadParts(options: MultipartUploadOptions): Promise<CompletedPart[]> {
  const { file, plan, transport } = options;
  const wait = options.wait ?? waitFor;
  const random = options.random ?? Math.random;

  const neededParts = Math.max(1, Math.ceil(file.size / plan.partSizeBytes));
  if (plan.partCount !== neededParts) {
    throw new ApiError('validation', 'This file is not the one the upload was started with.', {
      code: 'upload_file_mismatch',
    });
  }
  const sizeOf = (partNumber: number): number =>
    partNumber < plan.partCount ? plan.partSizeBytes : file.size - plan.partSizeBytes * (plan.partCount - 1);

  // A stored part counts only at the size this file gives it; anything else is re-sent.
  const etags = new Map<number, string>();
  for (const part of plan.uploadedParts) {
    if (
      part.partNumber >= 1 &&
      part.partNumber <= plan.partCount &&
      part.sizeBytes === sizeOf(part.partNumber)
    ) {
      etags.set(part.partNumber, part.etag);
    }
  }
  const pending = Array.from({ length: plan.partCount }, (_, index) => index + 1).filter(
    (partNumber) => !etags.has(partNumber)
  );

  const controller = new AbortController();
  const { signal } = controller;
  const outer = options.signal;
  if (outer?.aborted === true) throw abortReason(outer);
  const forwardAbort = (): void => controller.abort(outer === undefined ? undefined : abortReason(outer));
  outer?.addEventListener('abort', forwardAbort, { once: true });

  const storedBytes = [...etags.keys()].reduce((total, partNumber) => total + sizeOf(partNumber), 0);
  const sentBytes = new Map<number, number>();
  const report = (): void => {
    let sent = storedBytes;
    for (const bytes of sentBytes.values()) sent += bytes;
    options.onProgress?.({ sentBytes: sent, totalBytes: file.size });
  };

  // One signing call covers the next hundred parts, so workers share batches.
  const urls = new Map<number, Promise<string>>();
  const urlFor = (partNumber: number): Promise<string> => {
    const known = urls.get(partNumber);
    if (known !== undefined) return known;

    const batch = pending
      .filter((candidate) => candidate >= partNumber && !urls.has(candidate))
      .slice(0, URL_BATCH_SIZE);
    const signed = options.partUrls(batch, signal);
    for (const candidate of batch) {
      const url = signed.then((parts) => {
        const part = parts.find((entry) => entry.partNumber === candidate);
        if (part === undefined) throw new ApiError('server', `Core signed no URL for part ${candidate}.`);
        return part.url;
      });
      // Parts never reached after a failure must not surface as unhandled rejections.
      url.catch(() => undefined);
      urls.set(candidate, url);
    }
    return urls.get(partNumber) as Promise<string>;
  };

  const uploadPart = async (partNumber: number): Promise<void> => {
    const start = (partNumber - 1) * plan.partSizeBytes;
    const body = file.slice(start, start + sizeOf(partNumber));
    let renewed = false;

    for (let attempt = 1; ; attempt += 1) {
      try {
        const url = await urlFor(partNumber).catch((error: unknown) => {
          // A failed signing call must not be cached: the retry asks again.
          urls.delete(partNumber);
          throw error;
        });
        const etag = await transport.put(url, body, {
          signal,
          onProgress: (bytes) => {
            sentBytes.set(partNumber, Math.min(bytes, body.size));
            report();
          },
        });
        etags.set(partNumber, etag);
        sentBytes.set(partNumber, body.size);
        report();
        return;
      } catch (error) {
        if (signal.aborted) throw abortReason(signal);
        sentBytes.delete(partNumber);
        report();

        if (isRefusedUrl(error) && !renewed) {
          renewed = true;
          urls.delete(partNumber);
          attempt -= 1;
          continue;
        }
        if (!isRetryable(error) || attempt >= ATTEMPTS_PER_PART) throw error;
        await wait(BACKOFF_BASE_MS * 2 ** (attempt - 1) * (0.5 + random() / 2), signal);
      }
    }
  };

  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < pending.length && !signal.aborted) {
      const partNumber = pending[next] as number;
      next += 1;
      await uploadPart(partNumber);
    }
  };

  try {
    report();
    await Promise.all(Array.from({ length: Math.min(PARTS_IN_FLIGHT, pending.length) }, worker));
  } catch (error) {
    // The first failure, or the caller's abort, tears down the parts still in flight.
    if (!signal.aborted) controller.abort(error);
    throw abortReason(signal);
  } finally {
    outer?.removeEventListener('abort', forwardAbort);
  }

  return Array.from({ length: plan.partCount }, (_, index) => ({
    partNumber: index + 1,
    etag: etags.get(index + 1) as string,
  }));
}
